/**
 * PATH: packages/email-hub/src/feature/components/ComposeInboxRail.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-COMPOSEINBOXRAIL-001
 * PURPOSE: A compact, read-only "recent inbox" rail pinned to the RIGHT of the
 *   full-page compose surface so the operator can keep their inbox in view
 *   while writing — and collapse it to a thin strip when they want the room.
 * WHY:
 *   Operator 2026-06-23: "compose should open with [the] right slider of emails
 *   still active … and I can collapse it if I want." The full-page
 *   /email/compose route used to blank out the inbox entirely. This keeps the
 *   list visible (rows open in a NEW tab so the in-progress draft is never
 *   lost) with a persisted collapse toggle.
 *   BUG-2026-07-17-EMAIL-CONTEXTMENU-COVERAGE — right-click must open the
 *   canonical email menu here too (was a silent no-op).
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌────────────────────────┐
 *   │ useEmails    │ → │ recent inbox rows +   │ → │ collapsible right rail  │
 *   │ (folder:inbox)│   │ unread count          │   │ (new-tab links)         │
 *   └──────────────┘   └──────────────────────┘   └────────────────────────┘
 * RELATED:
 *   - ../pages/ComposePage.tsx (ComposePageRoute): renders this beside ComposePage.
 *   - ../hooks/useEmails: the inbox list source (same hook the Inbox uses).
 *   - ../lib/basePath (emailPath): host-correct read-route links.
 */
import { useCallback, useState } from 'react'
import { ChevronLeft, ChevronRight, Inbox, RefreshCw } from 'lucide-react'

import { useEmailAllocationMembers } from '../hooks/useEmailMembers'
import { emailPath } from '../lib/basePath'
import { SearchCommandRow } from '../pages/inbox-page/SearchCommandRow'
import { EmailContextMenuProvider } from './EmailContextMenu'
import FolderEmailRow from './FolderEmailRow'
import { KeyboardShortcutsPanel } from './KeyboardShortcutsPanel'
import { useComposeRailSearch } from './useComposeRailSearch'

// Collapse preference is operator-personal and host-agnostic — a single
// localStorage flag is enough (no need to widen the shared UI store).
const LS_KEY = 'qt:compose-inbox-rail:collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === '1'
  } catch {
    return false
  }
}

export interface ComposeInboxRailProps {
  /** Click handler for a row. When supplied the row opens the email
   *  IN PLACE (the compose page swaps the rail for a reader pane and
   *  keeps the draft mounted) instead of opening a new browser tab.
   *  Omitted → falls back to the draft-safe new-tab behaviour. */
  onOpenEmail?: (emailId: string) => void
  /** Currently previewed email id, so the matching row reads selected. */
  activeEmailId?: string | null
}

export function ComposeInboxRail({ onOpenEmail, activeEmailId }: ComposeInboxRailProps = {}) {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)
  const inviteMembers = useEmailAllocationMembers()
  const search = useComposeRailSearch()

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(LS_KEY, next ? '1' : '0')
      } catch {
        /* private-mode / quota — fall back to in-memory only */
      }
      return next
    })
  }, [])

  // Same useEmails query the Inbox search uses (composeRailEmailQuery). The
  // persistKey stays email:compose-rail so this list does not clobber the
  // inbox snapshot. Unread still prefers the server stat.
  const { emails, loadingInitial, refreshing, error, refetch } = search
  const unread = search.unread ?? emails.reduce((n, e) => (e.is_read ? n : n + 1), 0)

  // Hidden below lg — on narrow viewports compose already takes the whole
  // surface and a side rail would crush it.
  if (collapsed) {
    return (
      <div className="hidden lg:flex w-9 shrink-0 flex-col items-center border-l border-border bg-muted/30">
        <button
          type="button"
          onClick={toggle}
          aria-label="Show inbox"
          title="Show inbox"
          className="mt-2 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="mt-3 flex flex-col items-center gap-2">
          <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {unread > 0 && (
            <span
              className="rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-4 text-white"
              title={`${unread} unread`}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
        <span
          className="mt-2 select-none text-[10px] font-medium uppercase tracking-widest text-muted-foreground"
          style={{ writingMode: 'vertical-rl' }}
        >
          Inbox
        </span>
      </div>
    )
  }

  return (
    <EmailContextMenuProvider
      onRefetch={refetch}
      inviteMembers={inviteMembers ?? undefined}
    >
      {({ handleContextMenu, contextMenuNode }) => (
        <aside
          className="hidden lg:flex w-[400px] shrink-0 flex-col border-l border-border bg-background"
          data-testid="compose-inbox-rail"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span>Inbox</span>
              {unread > 0 && (
                <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold leading-4 text-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => refetch()}
                aria-label="Refresh inbox"
                title="Refresh"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                onClick={toggle}
                aria-label="Collapse inbox"
                title="Collapse"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="border-b border-border px-2 py-2">
            <SearchCommandRow vm={search.model} pinnedDensity="roomy" />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {loadingInitial ? (
              <div className="flex h-full items-center justify-center px-4 py-8 text-xs text-muted-foreground">
                Loading inbox…
              </div>
            ) : error ? (
              <div className="px-4 py-6 text-center text-xs text-destructive">
                Couldn’t load inbox.{' '}
                <button type="button" onClick={() => refetch()} className="underline">
                  Retry
                </button>
              </div>
            ) : emails.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                {search.model.searchQuery.trim()
                  ? 'No emails match this search.'
                  : 'No emails in your inbox.'}
              </div>
            ) : (
              // Use the SAME row the Inbox + every folder render (FolderEmailRow →
              // EmailRow) so the compose-page inbox looks identical to the hub
              // (operator 2026-06-29: "inbox panel at all places should look the
              // same"). Clicking a row opens it IN PLACE when the host provides
              // `onOpenEmail` (compose stays mounted on the left, draft live);
              // otherwise it falls back to a draft-safe new tab.
              <ul className="divide-y divide-border">
                {emails.map((row) => (
                  <li key={row.id}>
                    <FolderEmailRow
                      email={row}
                      isSelected={activeEmailId === row.id}
                      onSelect={
                        onOpenEmail
                          ? () => onOpenEmail(row.id)
                          : () =>
                              window.open(
                                emailPath(`/${row.id}`),
                                '_blank',
                                'noopener,noreferrer',
                              )
                      }
                      onContextMenu={(event) => handleContextMenu(event, row)}
                      onRefetch={refetch}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          {contextMenuNode}
          <KeyboardShortcutsPanel
            open={search.shortcutsOpen}
            onClose={search.closeShortcuts}
          />
        </aside>
      )}
    </EmailContextMenuProvider>
  )
}
